import { useMemo } from 'react'
import dayjs from 'dayjs'
import { Form, Formik } from 'formik'
import { useTranslation } from 'react-i18next'
import { dateConversions, KeyboardDatePickerInput } from '@dhl/inputs'
import { CheckboxInput as FCheckboxInput } from '@dhl/formik'
import { useGetReportMarketingHook } from '@dhl/mdp-api'
import { Button, Card, Checkbox, FormGroup, Grid, H6 } from '@dhl-ui/components'
import { downloadReport, useDataAsyncCall } from '~/api/admin-api'
import { groupByCondition, useNotificationMessage } from '~/common'
import { createStartDateEndDateValidationSchema } from '~/modules/reports/model/validation'

type Countries = {
  countries: string[]
}

export type MarketingFormValues = {
  interval: {
    from: Date | null
    till: Date | null
  }
  countries: Record<string, boolean>
  express: boolean
}

export const MarketingReportPage = () => {
  const { t } = useTranslation()
  const getData = useGetReportMarketingHook()

  const { showError } = useNotificationMessage()
  const [countries] = useDataAsyncCall<Countries>(`admin-user/possible-countries`)

  const onSubmit = async (values: MarketingFormValues) => {
    const startDate = dayjs(values.interval.from).format('YYYY-MM-DD')
    const endDate = dayjs(values.interval.till).format('YYYY-MM-DD')
    const countries = Object.keys(values.countries)
      .filter((country) => values.countries[country])
      .join(',')
    const products = values.express ? 'EXPRESS' : ''

    const response = await downloadReport(getData({ startDate, endDate, countries, products }), 'marketingData.csv')

    if (response) {
      showError(JSON.parse(response).message)
    }
  }

  const validationSchema = createStartDateEndDateValidationSchema(t)

  const initialValues: MarketingFormValues = useMemo(
    () => ({
      interval: {
        from: null,
        till: null,
      },
      countries: Object.assign({}, ...(countries?.countries || []).map((country: string) => ({ [country]: true }))),
      express: false,
    }),
    [countries]
  )

  return (
    <Formik
      initialValues={initialValues}
      onSubmit={onSubmit}
      validationSchema={validationSchema}
      validateOnChange={false}
      enableReinitialize
    >
      {({ isSubmitting, values }) => (
        <>
          <Card>
            <Card.Header title={t('admin.reportsPage.marketingData')} />
            <Card.Content>
              <Form data-testid="marketing-form">
                <Grid container spacing={2} direction="row" alignItems="center">
                  <Grid size={{ xs: 2 }}>
                    <H6>{t('admin.reportsPage.dateRange')}</H6>
                  </Grid>
                  <Grid container size={{ xs: 10 }} spacing={2}>
                    <Grid size={{ xs: 6 }}>
                      <KeyboardDatePickerInput
                        label={t('admin.reportsPage.startDate')}
                        name="interval.from"
                        conversions={dateConversions}
                        fullWidth
                      />
                    </Grid>
                    <Grid size={{ xs: 6 }}>
                      <KeyboardDatePickerInput
                        label={t('admin.reportsPage.endDate')}
                        name="interval.till"
                        conversions={dateConversions}
                        fullWidth
                      />
                    </Grid>
                  </Grid>
                  <Grid size={{ xs: 2 }}>
                    <H6>{t('admin.reportsPage.country')}</H6>
                  </Grid>
                  <Grid size={{ xs: 10 }} display="grid" gridTemplateColumns="auto auto auto auto auto">
                    {groupByCondition(Object.keys(values.countries), (_, index) => index % 10).map((group) => (
                      <FormGroup showMoreThreshold={8}>
                        {group.map((country) => (
                          <FCheckboxInput
                            key={String(country)}
                            name={`countries.${country}`}
                            label={t(`inputs.destinationInput.country_${country}`)}
                          />
                        ))}
                      </FormGroup>
                    ))}
                  </Grid>
                  <Grid size={{ xs: 2 }}>
                    <H6>{t('admin.reportsPage.products')}</H6>
                  </Grid>
                  <Grid size={{ xs: 10 }}>
                    <Checkbox name="express" label={t('admin.reportsPage.express')} />
                  </Grid>
                  <Grid size={{ xs: 12 }} sx={{ mt: 4 }} display="flex" justifyContent="flex-end">
                    <Button size="medium" disabled={isSubmitting} color="primary" type="submit" variant="contained">
                      {t('admin.reportsPage.downloadMarketing')}
                    </Button>
                  </Grid>
                </Grid>
              </Form>
            </Card.Content>
          </Card>
        </>
      )}
    </Formik>
  )
}
